import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _typeof from "@babel/runtime/helpers/typeof";
var _excluded = ["aria-label", "alt", "badgeProps", "className", "color", "clientId", "content", "email", "initials", "role", "showRing", "size", "src", "onClick", "tabIndex"];
import { useForkRef } from "@smartsheet/lodestar-utils";
import { LogActionId } from "@smartsheet/logging-state-service";
import { useCombinedThemeMode } from "@smartsheet/sds-theme-internal";
import React, { forwardRef, useState, useRef, useEffect } from "react";
import { neutralLight40 } from "../../constants/colors";
import { useLoggingDecorator, useLoggingDisabledByDefault } from "../../util/logging";
import { colorTheme, getColors, getBackground, getBackgroundHoverColor, avatarTheme } from "./avatarTheme";
import { avatarUtils } from "./avatarUtils";
import { AvatarBadge } from "./styled/AvatarBadge";
import { AvatarRoot } from "./styled/AvatarRoot";
import { Image } from "./styled/Image";
import { Initials } from "./styled/Initials";
var colorIsThemeColor = function colorIsThemeColor(color) {
  return color in colorTheme;
};
function parseColor(email, color, theme) {
  if (email) {
    var emailColor = avatarUtils.emailToAvatarColor(email, theme === "ursa-light" ? "ursa" : "lodestar");
    return getColors(emailColor, theme);
  }
  if (_typeof(color) === "object") {
    return _objectSpread(_objectSpread({}, color), {}, {
      backgroundHoverColor: color.backgroundColor,
      borderColor: color.backgroundColor
    });
  } else if (colorIsThemeColor(color)) {
    return getColors(color, theme);
  } else {
    // For back-compat with older code. Will not be accessible in all cases.
    return {
      backgroundColor: color,
      backgroundHoverColor: color,
      borderColor: color,
      textColor: neutralLight40
    };
  }
}

/**
 * An avatar is a visual representation of a user or entity, typically using a profile image or initials.
 */
export var Avatar = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var ariaLabel = _ref["aria-label"],
    alt = _ref.alt,
    badgeProps = _ref.badgeProps,
    className = _ref.className,
    color = _ref.color,
    clientId = _ref.clientId,
    content = _ref.content,
    email = _ref.email,
    initials = _ref.initials,
    _ref$role = _ref.role,
    role = _ref$role === void 0 ? "button" : _ref$role,
    _ref$showRing = _ref.showRing,
    showRing = _ref$showRing === void 0 ? false : _ref$showRing,
    size = _ref.size,
    src = _ref.src,
    onClick = _ref.onClick,
    _ref$tabIndex = _ref.tabIndex,
    tabIndex = _ref$tabIndex === void 0 ? 0 : _ref$tabIndex,
    rest = _objectWithoutProperties(_ref, _excluded);
  if (process.env.NODE_ENV !== "production") {
    if (typeof content === "string") {
      console.warn("Avatar: the content prop doesn't quite center text well due to nuances in" + " TT Norms. Please use the initials prop instead.");
    }
    if (initials && content) {
      console.warn("Avatar: both content and initials were provided to the Avatar component," + " content will take precedence. Please choose one or the other.");
    }
    if (!content && !initials && !src) {
      console.warn("Avatar: no content was provided to the Avatar component. Please include a" + " value for src, content, or initials");
    }
    if (email && color) {
      console.warn("Avatar: Please only provide a color OR an email. Not both.");
    }
  }
  var themeMode = useCombinedThemeMode();
  var localRef = useRef(null);
  var internalRef = useForkRef(ref, localRef);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasError = _useState2[0],
    setError = _useState2[1];
  var _useState3 = useState(undefined),
    _useState4 = _slicedToArray(_useState3, 2),
    customSize = _useState4[0],
    setCustomSize = _useState4[1];
  var withLogging = useLoggingDecorator({
    controlType: "ava",
    clientId: clientId
  });
  var onClickWithLogging = useLoggingDisabledByDefault(withLogging, {
    actionId: LogActionId.CLICK
  }, onClick);

  // Since we've deprecated the `alt` prop and are trying to instead use the aria-label prop,
  // Let's use the alt as the aria-label if it's not also specified.
  var actualLabel = ariaLabel || alt;

  // Default to blue if no color is provided here instead of in the prop destructuring so the
  // warning messages above have the option to see an unset color.
  var colors = parseColor(email, color !== null && color !== void 0 ? color : "blue", themeMode);
  useEffect(function () {
    if (localRef && localRef.current) {
      var sizeVar = getComputedStyle(localRef.current).getPropertyValue("--smar--sds--avatar--size");
      var _size = sizeVar ? parseInt(sizeVar, 10) : undefined;
      if (_size && _size !== customSize) {
        setCustomSize(_size);
      }
    }
  }, [localRef, customSize]);
  return /*#__PURE__*/React.createElement(AvatarRoot, _extends({
    className: className,
    "data-client-id": clientId
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    ,
    onClick: onClickWithLogging,
    role: role,
    customSize: customSize,
    size: size,
    ref: internalRef,
    tabIndex: tabIndex,
    "aria-label": actualLabel,
    showRing: showRing,
    themeMode: themeMode
  }, rest, {
    style: {
      "--smar--lc--avatar--background": getBackground({
        backgroundColor: colors.backgroundColor,
        backgroundImage: src,
        hasError: hasError
      }),
      "--smar--lc--avatar--background--hover": getBackgroundHoverColor({
        backgroundColor: colors.backgroundHoverColor,
        hasBackgroundImage: !!src,
        hasError: hasError
      }),
      "--smar--lc--avatar--border-color": colors.borderColor,
      "--smar--lc--avatar--text-color": colors.textColor
    }
  }), src && !hasError && /*#__PURE__*/React.createElement(Image, {
    src: src,
    alt: "",
    "aria-hidden": true,
    onError: function onError() {
      return setError(true);
    }
  }), (!src || hasError) && content, (!src || hasError) && !content && initials && /*#__PURE__*/React.createElement(Initials, null, initials), badgeProps && /*#__PURE__*/React.createElement(AvatarBadge, _extends({
    size: size === "medium" ? 18 : 22,
    backgroundColor: avatarTheme.badge.backgroundColor[badgeProps.type](themeMode),
    color: avatarTheme.badge.iconColor[badgeProps.type](themeMode),
    themeMode: themeMode
  }, badgeProps), badgeProps.icon));
});
Avatar.displayName = "Avatar";
//# sourceMappingURL=Avatar.js.map